import { business } from "@/data/business";
import { cn } from "@/lib/utils";

/** Single source of truth lives in business.financingUrl. */
const HEARTH_APPLY_URL = business.financingUrl;

const FORMATS = {
  wide: {
    // 852px = twice the widest display size (~426px) so retina stays sharp
    // without shipping the old 1400px file.
    src: "https://hearth.imgix.net/contractor-v2/banners/home_improvement-v2/700x110_darkblue.png?w=852&auto=compress,format",
    w: 700,
    h: 110,
    max: "max-w-[700px]",
  },
  square: {
    src: "https://hearth.imgix.net/contractor-v2/banners/home_improvement-v2/310x310_darkblue.png?dpr=2&auto=compress,format",
    w: 310,
    h: 310,
    max: "max-w-[420px]",
  },
} as const;

/**
 * Hearth financing banner — the artwork already contains its own
 * "GET MY RATES" button, so it IS the call to action.
 *
 * Placement policy (do not break these — they cause dead space / duplicate CTAs):
 * - Prefer <FinancingCta /> from blocks.tsx over using this component directly.
 *   It pairs the banner with the standard heading, one context line and the
 *   NMLS disclosure, so every page renders financing the same way.
 * - Never put another button or "check your rate" line beside the artwork; it
 *   already contains its own "GET MY RATES" button.
 * - `wide` (700x110) is the default and the only variant used site-wide.
 * - `square` (310x310) is rail-only: solely a sidebar that sits beside ~400px
 *   of content. If no such slot exists, use `wide`.
 * - The tall HearthCalculator widget belongs only where the opposite column is
 *   tall enough to match it: /financing and the /request-estimate financing
 *   band. Never in a sidebar or beside a short block.
 */
export function HearthBanner({
  className,
  format = "wide",
  note = "Check your rate in about a minute — no impact to your credit score.",
}: {
  className?: string;
  format?: "wide" | "square";
  /** Pass null to drop the supporting line. */
  note?: string | null;
}) {
  const f = FORMATS[format];

  return (
    <div className={cn("min-w-0", className)}>
      <a
        href={HEARTH_APPLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Check your financing options through Hearth — opens in a new tab"
        className={cn(
          "block w-full rounded-sm border border-border bg-background p-1.5 transition-all",
          "hover:-translate-y-0.5 hover:border-brand hover:shadow-lg",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          f.max,
        )}
      >
        <img
          src={f.src}
          alt="Check financing options through Hearth"
          width={f.w}
          height={f.h}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="h-auto w-full rounded-[2px]"
        />
      </a>
      {note ? (
        <p className={cn("mt-2 text-xs leading-relaxed text-muted-foreground", f.max)}>{note}</p>
      ) : null}
    </div>
  );
}
